import React, { useState, useEffect, forwardRef, useRef } from "react";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { Checkbox, FormControlLabel } from "@mui/material";
import './AssetsTree.scss';
import noAssets from "../../assets/no-single-level-msg.svg";

const TreeNode = ({ node, checkedNodes, onToggle }: any) => {
  const isChecked = checkedNodes.has(node.id);
  return (
    <TreeItem
      itemId={node.id}
      label={
        <div className="child-check">
          <div className="sub-check-fields">
            <div className="assets-name-section" style={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                style={{ marginTop: '-10px' }}
                checked={isChecked}
                onChange={() => onToggle(node.id)}
              />
              <div className="ch-lbl-section">
                <div className="name-labl text_ellipsis" title={node.assetName}>
                  <span style={{ marginRight: "6px", display: "inline-flex", alignItems: "center" }}>
                    <span style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: node.type === "Cloud" || node.type === "cloud" ? "#FE6568" : "#1976d2",
                      display: "inline-block",
                    }} />
                  </span>
                  {node.assetName}
                </div>
                <span className="vendor-name"
                  style={{
                    display: 'inline-block',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    verticalAlign: 'middle'
                  }}
                  title={node.region_name}>{node.region_name}</span>
              </div>
            </div>
          </div>
          <div className="vn-name">
  {node.vendorName === "Amazon web services" ? "AWS" : node.vendorName} - {node.assetType}
</div>
        </div>
      }
    />
  );
};

const AssetsTree = forwardRef(({ searchQuery, data, setfilterAssetsData, setselectedAssets, clearFilters,skipApiCall = false, preSelectedAssetIds = [] }: any, ref) => {
  const [checkedNodes, setCheckedNodes] = useState(new Set());

  const hasUserInteracted = useRef(false);

  const uniqueData = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    const seen = new Set();
    return data.filter((item: any) => {
      if (!item || !item.id) return false;
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [data]);

  const [filteredData, setFilteredData] = useState(uniqueData);
  console.log(preSelectedAssetIds,"preSelectedAssetIds");

  const clearInfo = () => {
    hasUserInteracted.current = false;
    setCheckedNodes(new Set());
    setfilterAssetsData([]);
    setselectedAssets(new Set());
  };

  React.useImperativeHandle(ref, () => ({
    triggerClear: clearInfo
  }));

  const preSelectedKey = React.useMemo(
    () => (preSelectedAssetIds || [])
      .map((a: any) => (typeof a === 'string' ? a : a?.id))
      .join(','),
    [preSelectedAssetIds]
  );

  // Handle skipApiCall = true scenario
  useEffect(() => {
    if (hasUserInteracted.current) return;
    if (skipApiCall && preSelectedAssetIds && preSelectedAssetIds.length > 0) {
      const initialChecked = new Set(preSelectedAssetIds.map((asset: any) => {
        return typeof asset === 'string' ? asset : asset.id;
      }));
      setCheckedNodes(initialChecked);
      setfilterAssetsData([...initialChecked]);
      setselectedAssets(initialChecked);
    }
  }, [skipApiCall, preSelectedKey, setfilterAssetsData, setselectedAssets]);

  // Handle skipApiCall = false scenario
  useEffect(() => {
    if (hasUserInteracted.current) return;
    if (!skipApiCall && !clearFilters) {
      let initialChecked = new Set();
      if (preSelectedAssetIds && preSelectedAssetIds.length > 0) {
        initialChecked = new Set(preSelectedAssetIds.map((asset: any) => {
          return typeof asset === 'string' ? asset : asset.id;
        }));
      } else {
        const selectedFromData = uniqueData.filter((node: any) => node.selected);
        initialChecked = new Set(selectedFromData.map((node: any) => node.id));
      }
      setCheckedNodes(initialChecked);
      setfilterAssetsData([...initialChecked]);
      setselectedAssets(initialChecked);
    }
  }, [uniqueData, preSelectedKey, setfilterAssetsData, skipApiCall, clearFilters]);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredData(uniqueData);
      return;
    }
    setFilteredData(
      uniqueData.filter((node: any) => JSON.stringify(node).toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [searchQuery, uniqueData]);

  const toggleCheck = (nodeId: any) => {
    hasUserInteracted.current = true;
    setCheckedNodes((prevChecked: any) => {
      const updatedChecked = new Set(prevChecked);
      if (updatedChecked.has(nodeId)) {
        updatedChecked.delete(nodeId);
      } else {
        updatedChecked.add(nodeId);
      }
      setfilterAssetsData([...updatedChecked]);
      setselectedAssets(updatedChecked);
      return updatedChecked;
    });
  };

  const handleSelectAll = (event: any) => {
    hasUserInteracted.current = true;
    let updatedChecked;
    if (event.target.checked) {
      updatedChecked = new Set(uniqueData.map((node: any) => node.id));
    } else {
      updatedChecked = new Set();
    }
    setCheckedNodes(updatedChecked);
    setfilterAssetsData([...updatedChecked]);
    setselectedAssets(updatedChecked);
  };

  const allAssetsChecked = checkedNodes.size > 0 && checkedNodes.size === uniqueData.length;
  const allAssetsIndeterminate = checkedNodes.size > 0 && checkedNodes.size < uniqueData.length;

  return (
    <>
      {filteredData && filteredData.length ? <div className="right-filter g-height scroll">
        <FormControlLabel
          sx={{
            "& .MuiTypography-root": { fontSize: "13px" }
          }}
          control={
            <Checkbox
              onChange={handleSelectAll}
              checked={allAssetsChecked}
              indeterminate={allAssetsIndeterminate}
            />
          }
          label="All Assets"
        />
        <SimpleTreeView
          sx={{
            "& .MuiTypography-root": { gap: 0 }
          }}
        >
          {filteredData.map((node: any) => (
            <TreeNode key={node.id} node={node} checkedNodes={checkedNodes} onToggle={toggleCheck} />
          ))}
        </SimpleTreeView>
      </div> :
        <div className="right-filter">
          <div className="no-single-level dark-image-sec">
            <img src={noAssets} alt="" />
            <div className="no-single-level-text">
              <p>No assets matched the selection.</p>
              <p>Please try a different selection.</p>
            </div>
          </div>
        </div>}
    </>
  );
});

export default AssetsTree;
